import { API_ENDPOINTS } from '../config/api';
import { apiFetch, getApiErrorMessage, getAuthHeaders } from './api';
import { snapshotStoreProduct, getProductLocalizedName } from './products';

export class StoreCartError extends Error {
  constructor(message, code) {
    super(message);
    this.code = code;
  }
}

function unwrapList(json) {
  const data = json?.data ?? json;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

async function parseJsonResponse(res) {
  const json = await res.json().catch(() => ({}));
  if (res.status === 401) {
    throw new StoreCartError('Session expired. Please log in again.', 'LOGIN_REQUIRED');
  }
  if (!res.ok || json?.success === false) {
    throw new Error(getApiErrorMessage(json, 'Cart request failed'));
  }
  return json;
}

function isProductCartRow(raw) {
  if (!raw || typeof raw !== 'object') return false;
  const productId = raw.productId || raw.product?.id || raw.productDetails?.id;
  if (!productId) return false;
  if (raw.poojaDetailsId != null && raw.poojaDetailsId !== '') return false;
  return true;
}

export function sameProductId(a, b) {
  return String(a || '') === String(b || '');
}

function productFromCartRow(raw) {
  const embedded =
    raw.product ||
    raw.productDetails ||
    raw.productDetail ||
    raw.productInfo ||
    raw;
  const snapshot = snapshotStoreProduct(embedded);
  const productId = String(raw.productId || embedded?.id || snapshot?.id || '');
  if (snapshot) {
    return {
      ...snapshot,
      id: productId || snapshot.id,
      // Cart API `quantity` is line qty, not stock — do not cap +/- at 1.
      maxQuantity: 99,
    };
  }
  if (!productId) return null;
  return {
    id: productId,
    productName: getProductLocalizedName(embedded?.productName || embedded?.name),
    variantName: getProductLocalizedName(embedded?.variantName),
    thumbnailImage: embedded?.thumbnailImage || embedded?.imageUrl || '',
    finalSellingPrice:
      embedded?.finalSellingPrice ??
      embedded?.final_selling_price ??
      embedded?.unitPrice ??
      embedded?.unit_price,
    sellingPrice: embedded?.sellingPrice ?? embedded?.selling_price ?? embedded?.price,
    outOfStock: embedded?.outOfStock,
    maxQuantity: 99,
  };
}

/** Group cart API rows into store cart lines keyed by productId. */
export function normalizeStoreCartItems(rawItems) {
  const map = new Map();
  for (const raw of rawItems) {
    if (!isProductCartRow(raw)) continue;
    const productId = String(raw.productId || raw.product?.id || '');
    if (!productId) continue;
    const parsedQty = Number(raw.quantity ?? raw.qty);
    const qty = Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 1;
    const product = productFromCartRow(raw);
    const existing = map.get(productId);
    if (existing) {
      existing.quantity += qty;
      if (product?.productName) existing.product = { ...existing.product, ...product };
    } else {
      map.set(productId, {
        productId,
        quantity: qty,
        product: product || { id: productId, productName: '', thumbnailImage: '', maxQuantity: 99 },
      });
    }
  }
  return Array.from(map.values());
}

export async function fetchStoreCartItems() {
  const res = await apiFetch(API_ENDPOINTS.cart, { skipAuth: false });
  if (res.status === 401) {
    throw new StoreCartError('Please log in to view your cart.', 'LOGIN_REQUIRED');
  }
  const json = await parseJsonResponse(res);
  return normalizeStoreCartItems(unwrapList(json));
}

export async function addProductToCart(productId) {
  if (!productId) throw new Error('Product ID is required');
  const res = await apiFetch(API_ENDPOINTS.cart, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ productId: String(productId) }),
  });
  return parseJsonResponse(res);
}

export async function getCartProduct(productId) {
  if (!productId) throw new Error('Product ID is required');
  const res = await apiFetch(API_ENDPOINTS.cartProduct(productId), { skipAuth: false });
  const json = await parseJsonResponse(res);
  const data = json?.data ?? json;
  return normalizeStoreCartItems([data])[0] || null;
}

export async function removeProductFromCart(productId) {
  if (!productId) throw new Error('Product ID is required');
  const res = await apiFetch(API_ENDPOINTS.cartProduct(productId), {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return parseJsonResponse(res);
}

/** Set quantity using add/remove endpoints (no PATCH on cart products). */
export async function setCartProductQuantity(productId, targetQty, currentQty = 0) {
  const max = 99;
  const qty = Math.min(max, Math.max(0, Number(targetQty) || 0));
  const current = Math.max(0, Number(currentQty) || 0);
  if (qty === current) return;

  if (qty === 0) {
    if (current > 0) await removeProductFromCart(productId);
    return;
  }

  if (qty > current) {
    const adds = qty - current;
    for (let i = 0; i < adds; i += 1) {
      await addProductToCart(productId);
    }
    return;
  }

  await removeProductFromCart(productId);
  for (let i = 0; i < qty; i += 1) {
    await addProductToCart(productId);
  }
}

export async function clearStoreCartItems(productIds = []) {
  const ids = [...new Set(productIds.filter(Boolean).map(String))];
  await Promise.all(ids.map((id) => removeProductFromCart(id)));
}

export const GUEST_CART_STORAGE_KEY = 'daiv_store_cart_guest';

export function readGuestCartItems() {
  try {
    const raw = localStorage.getItem(GUEST_CART_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function writeGuestCartItems(items) {
  try {
    localStorage.setItem(GUEST_CART_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore
  }
}

export function addGuestCartLine(items, snapshot, quantity = 1) {
  const productId = String(snapshot.id);
  const addQty = Math.max(1, Math.min(Number(quantity) || 1, snapshot.maxQuantity || 99));
  const list = Array.isArray(items) ? [...items] : [];
  const idx = list.findIndex((row) => sameProductId(row.productId, productId));
  if (idx === -1) {
    list.push({ productId, product: { ...snapshot, id: productId }, quantity: addQty });
  } else {
    const max = snapshot.maxQuantity || 99;
    list[idx] = {
      ...list[idx],
      productId,
      product: { ...snapshot, id: productId },
      quantity: Math.min(max, Number(list[idx].quantity || 0) + addQty),
    };
  }
  return list;
}

export function setGuestCartLineQuantity(items, productId, quantity) {
  const id = String(productId || '');
  const list = Array.isArray(items) ? [...items] : [];
  const row = list.find((item) => sameProductId(item.productId, id));
  if (!row) return list;
  const max = row.product?.maxQuantity || 99;
  const qty = Math.min(max, Math.max(0, Number(quantity) || 0));
  if (qty <= 0) return list.filter((item) => !sameProductId(item.productId, id));
  return list.map((item) =>
    sameProductId(item.productId, id) ? { ...item, productId: id, quantity: qty } : item
  );
}

export function removeGuestCartLine(items, productId) {
  const id = String(productId || '');
  return (Array.isArray(items) ? items : []).filter((row) => !sameProductId(row.productId, id));
}

/** Push guest cart lines to backend after login. */
export async function syncGuestCartToApi(guestItems = []) {
  for (const row of guestItems) {
    const qty = Math.max(1, Number(row.quantity) || 1);
    for (let i = 0; i < qty; i += 1) {
      await addProductToCart(row.productId);
    }
  }
}
